import { useAppSelector } from '../../redux/hooks';
import React, { useEffect, useMemo } from 'react';
import { analysisSelector } from '../../redux/selectors/analyses';
import { SingleSelectMenu } from '../Dropdown';
import { generateExpressionCohortMeasureOptions } from '../Builder/Cohorts/CohortRow/utils';
import { parseAliasReplacements } from '../../utils/redux/util';
import { MaybeNil } from '../../utils/interfaces';
import useAnalysisBuilderInfo from '../../redux/selectorHooks/useAnalysisBuilderInfo';
import { assertCondition, assertNotNil } from '../../utils/types';
import { IUserAliasReplacementFieldsFragment } from '../../utils/apollo/graphqlSchema';

interface IProps {
  measureId: MaybeNil<string>;
  analysisId: string;
  onMeasureChange: (measureId: string) => void;
  aliasReplacements: IUserAliasReplacementFieldsFragment[];
}

const ExpressionMeasureMenu = ({
  measureId,
  analysisId,
  onMeasureChange,
  aliasReplacements,
}: IProps) => {
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertNotNil(analysisBuilderInfo);
  const builderInfo = analysisBuilderInfo.query;
  assertCondition(builderInfo.__typename === 'ExpressionQuery');
  const { measure } = builderInfo;

  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, analysisId),
  );

  const options = useMemo(() => {
    // If applicable, we have to apply aliases to measure names:
    // 1. First we will try to get aliasReplacements from chartData in case
    // if we opened Query report, renamed measures, and we are using same Query
    // in Builder
    // 2. Get aliasReplacements from chart pref
    if (
      currentAnalysis?.chartData &&
      'aliasReplacements' in currentAnalysis?.chartData
    ) {
      return generateExpressionCohortMeasureOptions(
        measure,
        currentAnalysis?.chartData?.aliasReplacements,
      );
    }

    const parsedAliasReplacements = parseAliasReplacements(aliasReplacements);

    return generateExpressionCohortMeasureOptions(
      measure,
      parsedAliasReplacements,
    );
  }, [aliasReplacements, currentAnalysis, measure]);

  // assign default value as first option when render component if measure
  // is not selected
  useEffect(() => {
    if (!measureId && options.length > 0) {
      onMeasureChange(options[0].value);
    }
  }, [measureId, options, onMeasureChange]);

  // no need to display if only one option, for example: simple measure
  if (options.length === 1) {
    return null;
  }

  return (
    <SingleSelectMenu
      options={options}
      value={measureId}
      onChange={(measureId) => onMeasureChange(measureId)}
    />
  );
};

export default ExpressionMeasureMenu;
